import { err, ok } from 'neverthrow';
import { AST } from '../../ast';
import { Assert } from '../../utils';
import { TokenMeta, WithMeta, WithRange } from '../lex/types';
import {
  AttrAccess,
  Let,
  Call,
  Closure,
  CompileOutput,
  EraQlAst,
  Item,
  List,
  ParseCtx,
  ParseError,
  ParseRes,
  Assoc,
  Bindings,
} from './ast';
import { IdentToken, LiteralToken, SymbolToken, TagToken } from '../lex/tokens';
import {
  FunctionIdentifier,
  isFunctionIdentifier,
} from '../../ast/func-identifier';
import _ from 'lodash';
import { Ty } from '../../ty';
import { SPECIAL_FORMS } from '../special-forms';
import { Relation } from '../../builder/relation';
import { Expression } from '../../builder/expression';
import { Constant, EmptyArrayOf } from '../../builder/utilities';
import { parseTy } from '../parse';
import { Markup } from '../../builder';
import { ParseFunc, RecordOf } from '../special-forms/combinators';

// TODO: change the type of compileAst's first arg to WithRange<EraQlast>
// because comments are not needed for compilation
export const compileAst = (
  [ast, meta]: WithMeta<EraQlAst | Item>,
  ctx: ParseCtx
): ParseRes<CompileOutput> => {
  switch (ast.t) {
    case 'literal':
      return compileLiteral([ast, meta], ctx);
    case 'symbol':
      return compileSym([ast, meta], ctx);
    case 'ident':
      return compileIdent([ast, meta], ctx);
    case 'call':
      return compileCall([ast, meta], ctx);
    case 'list':
      return compileList([ast, meta], ctx);
    case 'assoc':
      return compileAssoc([ast, meta], ctx);
    case 'let':
      return compileLet([ast, meta], ctx);
    case 'closure':
      return compileClosure([ast, meta], ctx);
    case 'attr-access':
      return compileSymbolAccess([ast, meta], ctx);
    default:
      return ok({ item: ast });
  }
};

const compileSymbolAccess = (
  [ast, meta]: WithMeta<AttrAccess>,
  ctx: ParseCtx
): ParseRes<CompileOutput> => {
  const inRes = expectSymbolNamespace(ast.in, ctx);
  if (inRes.isErr()) {
    return err(inRes.error);
  }

  const name = ast.attr[0].name;
  const notFoundError: WithMeta<ParseError> = [
    { t: 'attribute-not-found', name },
    meta,
  ];

  // TODO later: merge implementations of symbol-map and symbol-lookup
  switch (inRes.value.t) {
    case 'item-map': {
      const result = inRes.value.map[name];

      if (result === undefined) {
        return err([notFoundError]);
      }

      return ok(result);
    }

    case 'symbol-lookup': {
      const res = inRes.value.lookup(name, meta);

      if (res === null) {
        return err([notFoundError]);
      }
      if (res.isErr()) {
        return err(res.error.map((e) => [e, meta]));
      }

      return ok({ item: res.value });
    }

    default:
      return Assert.unreachable(inRes.value);
  }
};

const compileClosure = (
  [ast, _meta]: WithMeta<Closure>,
  ctx: ParseCtx
): ParseRes<CompileOutput> => {
  const item: Item = {
    t: 'closure-with-bindings',
    closure: ast,
    bindings: ctx.bindings ?? {},
  };
  return ok({ item });
};

const compileLet = ([ast, _meta]: WithMeta<Let>, ctx: ParseCtx) => {
  const { sym, bindingExpr, body } = ast;
  const [{ sym: ident }, _meta1] = sym;

  const bindingCompiled = compileAst(bindingExpr, ctx);
  if (bindingCompiled.isErr()) {
    return err(bindingCompiled.error);
  }

  const newBinding = { [ident]: bindingCompiled.value.item };

  return compileAst(body, {
    ...ctx,
    bindings: { ...ctx.bindings, ...newBinding },
  });
};

const compileAssoc = (
  [ast, meta]: WithMeta<Assoc>,
  ctx: ParseCtx
): ParseRes<CompileOutput> => {
  return RecordOf(compileAst)([ast, meta], ctx).andThen((compiledRecord) => {
    const map: Record<string, CompileOutput> = _.mapValues(
      compiledRecord,
      (val) => val.v[0]
    );
    return ok<CompileOutput>({ item: { t: 'item-map', map } });
  });
};

const compileList = (
  [ast, _meta]: WithMeta<List>,
  ctx: ParseCtx
): ParseRes<CompileOutput> =>
  ctxAwareCompileMany(ast.elems, compileAst, ctx).map((items) => {
    const res: CompileOutput = {
      item: { t: 'item-list', items },
    };
    return res;
  });

const compileCall = (
  call: WithMeta<Call>,
  ctx: ParseCtx
): ParseRes<CompileOutput> => {
  const [ast, meta] = call;
  const { op, args } = ast;
  const [opToken, opMeta] = op;

  switch (opToken.t) {
    case 'tag':
      return callTag([opToken, opMeta], args, ctx);
    case 'operator':
    case 'symbol': {
      const sym = opToken.t === 'symbol' ? opToken.sym : opToken.op;

      const existingBinding = ctx.bindings?.[sym];

      if (existingBinding) {
        if (existingBinding.t === 'special-form') {
          return existingBinding.sp(args, ctx, call);
        } else if (existingBinding.t === 'closure-with-bindings') {
          const argsRes = ctxAwareCompileMany(
            args,
            ([ast, meta], ctx) =>
              compileAst([ast, meta], ctx).map(
                (output): WithMeta<CompileOutput> => [output, meta]
              ),
            ctx
          );

          return argsRes.isOk()
            ? callClosure(
                existingBinding.closure,
                existingBinding.bindings,
                argsRes.value,
                opMeta
              )
            : err(argsRes.error);
        }
      }

      const spForm = SPECIAL_FORMS[sym];

      if (spForm) {
        const [arity, fn] = spForm;

        if (arity !== 'any') {
          if (typeof arity === 'number') {
            if (arity !== args.length) {
              return err([[{ t: 'invalid-arity' }, opMeta]]);
            }
          } else if (!arity.includes(args.length)) {
            return err([[{ t: 'invalid-arity' }, opMeta]]);
          }
        }

        return fn(args, ctx, [ast, meta]);
      }

      if (isFunctionIdentifier(sym)) {
        return callFn([sym, opMeta], args, ctx);
      }

      return err([[{ t: 'cant-call' }, opMeta]]);
    }
    case 'closure': {
      const argsRes = ctxAwareCompileMany(
        args,
        ([ast, meta], ctx) =>
          compileAst([ast, meta], ctx).map(
            (output): WithMeta<CompileOutput> => [output, meta]
          ),
        ctx
      );

      return argsRes.isOk()
        ? callClosure(opToken, ctx.bindings ?? {}, argsRes.value, opMeta)
        : err(argsRes.error);
    }
    case 'call':
    case 'literal':
    case 'ident':
    case 'assoc':
    case 'list':
      return err([[{ t: 'cant-call' }, opMeta]]);
    case 'attr-access':
    case 'let': {
      throw new Error('TODO');
    }
    default:
      return Assert.unreachable(opToken);
  }
};

const callFn = (
  [op, meta]: WithMeta<FunctionIdentifier>,
  argsAst: readonly WithMeta<EraQlAst>[],
  ctx: ParseCtx
): ParseRes<CompileOutput> => {
  const argsRes = ctxAwareCompileMany(argsAst, compileToExpr, ctx);
  if (argsRes.isErr()) {
    return err(argsRes.error);
  }

  const expr: AST._FunctionCall<AST.ExprMacroChildren> = {
    t: 'function-call',
    op,
    args: argsRes.value.map((arg) => arg.ast),
  };

  const exprRes = Expression.tryfromAst(expr)
    .map((expr): CompileOutput => ({ item: { t: 'expr', expr } }))
    .mapErr((trace) => {
      const e: WithMeta<ParseError> = [{ t: 'type-check-error', trace }, meta];
      return [e];
    });

  return exprRes;
};

export const expectMu = (
  [arg, argMeta]: WithMeta<EraQlAst | Item>,
  ctx: ParseCtx
): ParseRes<Markup> =>
  compileAst([arg, argMeta], ctx).andThen((output): ParseRes<Markup> => {
    const { item } = output;
    switch (item.t) {
      case 'expr': {
        return ok(
          Markup.fromAst({ t: 'text', text: item.expr.ast, modifier: 'none' })
        );
      }
      case 'rel': {
        const mu = item.rel.chart.DataGrid();
        return ok(mu);
      }
      case 'mu':
        return ok(item.mu);
      default: {
        const e: ParseError = {
          t: 'expected',
          expected: 'markup',
        };
        return err([[e, argMeta]]);
      }
    }
  });

export const expectRel = (
  [arg, argMeta]: WithMeta<EraQlAst | Item>,
  ctx: ParseCtx
): ParseRes<Relation> =>
  compileAst([arg, argMeta], ctx).andThen((output): ParseRes<Relation> => {
    const { item } = output;
    switch (item.t) {
      case 'rel':
        return ok(item.rel);
      default: {
        const e: ParseError = {
          t: 'expected',
          expected: 'relation',
        };
        return err([[e, argMeta]]);
      }
    }
  });

type SymbolNamespaceItem =
  | Extract<Item, { t: 'item-map' }>
  | Extract<Item, { t: 'symbol-lookup' }>;

export const expectSymbolNamespace = (
  [arg, argMeta]: WithMeta<EraQlAst>,
  ctx: ParseCtx
) =>
  compileAst([arg, argMeta], ctx).andThen(
    (output): ParseRes<SymbolNamespaceItem> => {
      const { item } = output;
      if (item.t === 'item-map' || item.t == 'symbol-lookup') {
        return ok(item);
      }

      const e: ParseError = {
        t: 'expected',
        expected: 'symbol namespace',
      };
      return err([[e, argMeta]]);
    }
  );

/**
 * Given an EraQLAst, compile it down to an Expression.
 */
export const compileToExpr = (
  [arg, argMeta]: WithMeta<EraQlAst | Item>,
  ctx: ParseCtx
): ParseRes<Expression> =>
  compileAst([arg, argMeta], ctx).andThen((res) =>
    exprOfItem([res.item, argMeta], ctx)
  );

// TODO: this function is kinda bad since it doesn't propogate real errors up
// the stack (there is no reason to return a supplied error to a caller???)
export function exprOfItem(
  [item, itemMeta]: WithMeta<Item>,
  ctx: ParseCtx
): ParseRes<Expression> {
  switch (item.t) {
    case 'expr':
      return ok(item.expr);
    case 'ty':
      return ok(Constant(Ty.displayTy(item.ty)));
    case 'item-list': {
      const elems: Expression[] = [];
      const errs: WithRange<ParseError>[] = [];

      for (const x of item.items) {
        const res = exprOfItem([x.item, itemMeta], ctx);
        if (res.isOk()) {
          elems.push(res.value);
        } else {
          if (ctx.eager) {
            return err(res.error);
          } else {
            errs.push(...res.error);
          }
        }
      }

      if (errs.length > 0) {
        return err(errs);
      }

      if (elems.length === 0) {
        return ok(EmptyArrayOf('int'));
      }

      const arr: AST._MakeArray<AST.ExprMacroChildren> = {
        t: 'make-array',
        elements: elems.map(({ ast }) => ast),
      };

      const expr = Expression.tryfromAst(arr);

      if (expr.isErr()) {
        const e: ParseError = { t: 'type-check-error', trace: expr.error };
        return err([[e, itemMeta]]);
      }

      return ok(expr.value);
    }
    case 'item-map': {
      const fields: Record<string, Expression> = {};
      const errs: WithRange<ParseError>[] = [];

      for (const [key, val] of Object.entries(item.map)) {
        const res = exprOfItem([val.item, itemMeta], ctx);
        if (res.isOk()) {
          fields[key] = res.value;
        } else {
          if (ctx.eager) {
            return err(res.error);
          } else {
            errs.push(...res.error);
          }
        }
      }

      const ast: AST.ExprFR = {
        t: 'make-struct',
        fields: _.mapValues(fields, (x) => x.ast),
      };
      return errs.length === 0 ? ok(Expression.fromAst(ast)) : err(errs);
    }
    default: {
      return err([[{ t: 'expected', expected: 'expression' }, itemMeta]]);
    }
  }
}

export const expectOrderBy: ParseFunc<{
  expr: Expression;
  direction: AST.SortDirection;
}> = (
  [arg, argMeta],
  ctx: ParseCtx
): ParseRes<{ expr: Expression; direction: AST.SortDirection }> => {
  if (arg.t === 'order-by') {
    return ok(arg);
  }

  const res = compileAst([arg, argMeta], ctx);

  if (res.isErr()) {
    return err(res.error);
  }

  const { item } = res.value;

  switch (item.t) {
    case 'order-by':
      return ok(item);
    case 'expr':
      return ok({ expr: item.expr, direction: 'asc' });
    default: {
      const e: ParseRes<never> = err([
        [{ t: 'expected', expected: 'ordering' }, argMeta],
      ]);
      return e;
    }
  }
};

export const expectTy = (
  [arg, argMeta]: WithMeta<EraQlAst | Item>,
  ctx: ParseCtx
): ParseRes<Ty.ExtendedAttributeType> => {
  const res = compileAst([arg, argMeta], ctx);

  if (res.isErr()) {
    return err(res.error);
  }

  const e: ParseRes<never> = err([
    [{ t: 'expected', expected: 'type' }, argMeta],
  ]);

  const { item } = res.value;

  switch (item.t) {
    case 'ty':
      return ok(item.ty);
    case 'expr': {
      const { ast } = item.expr;
      if (ast.t === 'scalar' && typeof ast.val === 'string') {
        const parseRes = parseTy(ast.val, {
          ...ctx,
          offset: argMeta.range !== null ? argMeta.range[0] + 1 : undefined,
        });
        return parseRes;
      }
      return e;
    }
    default:
      return e;
  }
};

const callTag = (
  [{ tag }, tagMeta]: WithMeta<TagToken>,
  args: readonly WithMeta<EraQlAst>[],
  ctx: ParseCtx
): ParseRes<CompileOutput> => {
  const [arg, ...rest] = args;
  if (arg === undefined || rest.length > 0) {
    return err([[{ t: 'invalid-arity' }, tagMeta]]);
  }

  const argRes = compileAst(arg, ctx);

  if (argRes.isErr()) {
    return err(argRes.error);
  }

  const inner = argRes.value;

  switch (inner.item.t) {
    case 'ty':
      return ok({
        item: { t: 'ty', ty: Ty.tag(inner.item.ty, [tag]) },
      });
    case 'expr':
    case 'item-map': {
      const innerExpr = exprOfItem([inner.item, tagMeta], ctx);

      if (innerExpr.isErr()) {
        return err(innerExpr.error);
      }

      const expr: AST._FunctionCall<AST.ExprMacroChildren> = {
        t: 'function-call',
        op: 'tag',
        args: [innerExpr.value.ast, Constant(tag).ast],
      };

      return Expression.tryfromAst(expr)
        .map((expr): CompileOutput => ({ item: { t: 'expr', expr } }))
        .mapErr((trace) => {
          const e: WithMeta<ParseError> = [
            { t: 'type-check-error', trace },
            tagMeta,
          ];
          return [e];
        });
    }
    default:
      return err([[{ t: 'cant-tag' }, tagMeta]]);
  }
};

const compileIdent = (
  [{ name }, meta]: WithMeta<IdentToken>,
  ctx: ParseCtx
): ParseRes<CompileOutput> => {
  let attr: ParseRes<Ty.ExtendedAttributeType | Expression> | null = null;
  if (typeof ctx.attributes === 'function') {
    const res = ctx.attributes(name, meta);
    if (res !== null) {
      attr = res.mapErr((errs) => errs.map((e) => [e, meta]));
    }
  } else {
    const attrs =
      ctx.attributes instanceof Relation
        ? ctx.attributes.ref('from').star()
        : ctx.attributes;

    const res = attrs?.[name] ?? null;
    if (res !== null) {
      attr = ok(res);
    }
  }

  if (attr === null) {
    return err([[{ t: 'attribute-not-found', name }, meta]]);
  }

  return attr.map((x): CompileOutput => {
    const expr =
      x instanceof Expression
        ? x
        : Expression.fromAst({ t: 'attr', ty: x, name, source: 'from' });
    return { item: { t: 'expr', expr } };
  });
};

const compileLiteral = (
  [{ val }, _meta]: WithMeta<LiteralToken>,
  _ctx: ParseCtx
): ParseRes<CompileOutput> => {
  return ok({ item: { t: 'expr', expr: Constant(val) } });
};

const compileSym = (
  [{ sym }, meta]: WithMeta<SymbolToken>,
  ctx: ParseCtx
): ParseRes<CompileOutput> => {
  switch (sym) {
    case 'int':
    case 'super':
    case 'string':
    case 'timestamp':
    case 'float':
    case 'day':
    case 'month':
    case 'year':
    case 'boolean':
      return ok({ item: { t: 'ty', ty: Ty.ty(sym) } });
    default: {
      const binding = ctx.bindings?.[sym];

      if (binding) {
        return ok({ item: binding });
      }

      if (ctx.dynamicSymbolLookup) {
        const lookup = ctx.dynamicSymbolLookup(sym, meta);

        if (lookup) {
          return lookup
            .map((item) => ({ item }))
            .mapErr((errs) => errs.map((e) => [e, meta]));
        }
      }

      const e: ParseError = {
        t: 'unbound-symbol',
        sym,
        bindings: ctx.bindings ?? {},
      };

      return err([[e, meta]]);
    }
  }
};

export const callClosure = (
  closure: Closure,
  bindings: Bindings,
  argItems: readonly WithMeta<CompileOutput>[],
  func: TokenMeta
): ParseRes<CompileOutput> => {
  const {
    args: [args, _meta],
    body,
  } = closure;
  const newBindings: [string, Item][] = [];
  const syms = args.map(({ sym }) => sym);

  for (const [sym, argItem] of _.zip(syms, argItems)) {
    if (sym === undefined && argItem !== undefined) {
      const e: ParseError = {
        t: 'invalid-arity',
        allowed: [args.length],
        got: argItems.length,
      };
      return err([[e, argItem[1]]]);
    }

    if (sym !== undefined && argItem === undefined) {
      const e: ParseError = {
        t: 'invalid-arity',
        allowed: [args.length],
        got: argItems.length,
      };
      return err([[e, func]]);
    }

    Assert.assert(sym !== undefined && argItem !== undefined);

    newBindings.push([sym[0], argItem[0].item]);
  }

  return compileAst(body, {
    bindings: {
      ...bindings,
      ...Object.fromEntries(newBindings),
    },
  });
};

const ctxAwareCompileMany = <T>(
  asts: readonly WithMeta<EraQlAst>[],
  compileWith: (x: WithMeta<EraQlAst>, ctx: ParseCtx) => ParseRes<T>,
  ctx: ParseCtx
): ParseRes<T[]> => {
  const items: T[] = [];
  const errs: WithRange<ParseError>[] = [];

  for (const elem of asts) {
    const res = compileWith(elem, ctx);
    if (res.isOk()) {
      items.push(res.value);
    } else {
      if (ctx.eager) {
        return err(res.error);
      } else {
        errs.push(...res.error);
      }
    }
  }

  return errs.length === 0 ? ok(items) : err(errs);
};
